import { fromZonedTime } from 'date-fns-tz'

import { getChartMarketSession } from '@/lib/tradingview/datafeeds/chart-market-session'
import { Exchange } from '@/types/generated/common/types'
import type { VenueSchedule } from '@/types/generated/public_api'

/**
 * Databento's session venues. Their candle feed carries the last price forward through hours
 * the market is shut (zero volume, open = high = low = close), which charts draw as a dotted
 * flat run across every night, weekend and holiday — and includes extended-hours trades.
 */
const DATABENTO_SESSION_EXCHANGES = new Set<Exchange>([
  Exchange.EXCHANGE_GLBX,
  Exchange.EXCHANGE_XNAS,
  Exchange.EXCHANGE_XNYS,
  Exchange.EXCHANGE_OPRA,
])

export function dropsClosedMarketBars(exchange: Exchange): boolean {
  return DATABENTO_SESSION_EXCHANGES.has(exchange)
}

export interface CandleLike {
  /** Bar start, unix seconds. */
  timeSec: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

/** True when the bar should be dropped. */
export type ClosedMarketBarFilter = (bar: CandleLike) => boolean

// Beyond a week every bar spans a session; skip the date walk.
const MAX_FILTERED_INTERVAL_SEC = 7 * 86400
const DAY_MS = 86400 * 1000

const dateFormatters = new Map<string, Intl.DateTimeFormat>()
/** Local calendar date (YYYY-MM-DD) of an instant in `timeZone`. */
function localDate(ms: number, timeZone: string): string {
  let formatter = dateFormatters.get(timeZone)
  if (!formatter) {
    // en-CA formats as YYYY-MM-DD.
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dateFormatters.set(timeZone, formatter)
  }
  return formatter.format(ms)
}

const shiftDate = (date: string, days: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)

/** The schedule's YYYYMMDD → YYYY-MM-DD, the key format used here. */
const isoDate = (yyyymmdd: string) =>
  `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`

/** 0 = Monday … 6 = Sunday, the schedule's own numbering. */
const weekdayOf = (date: string) => (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7

/**
 * Drops bars from hours the venue was closed for the whole interval, per its schedule: weekly
 * sessions, holidays, dated overrides (early closes). How much counts as closed follows the
 * TV chart's session for the venue (getChartMarketSession), so both charts show the same bars:
 *
 *  - A regular session (XNAS/XNYS/OPRA `0930-1600`): every bar outside it goes, trades or not
 *    — TV shows regular hours only, so pre/post-market is cut here too.
 *  - `24x7` (GLBX: the schedule lists pit hours, yet Globex trades nearly round the clock):
 *    only carried-forward fillers (no volume, flat) go; real overnight trades stay.
 *
 * Flat quiet bars inside a session always stay.
 *
 * Null when there's nothing to filter: an always-open venue, no weekly sessions, or an
 * interval of more than a week.
 */
export function createClosedMarketBarFilter(
  schedule: VenueSchedule | undefined,
  intervalSec: number
): ClosedMarketBarFilter | null {
  if (!schedule || schedule.alwaysOpen || schedule.weeklySessions.length === 0) return null
  if (!(intervalSec > 0) || intervalSec > MAX_FILTERED_INTERVAL_SEC) return null

  const { timezone } = schedule
  const regularHoursOnly = getChartMarketSession(schedule.exchange).session !== '24x7'
  const holidays = new Set(schedule.holidays.map(isoDate))
  const overrides = new Map(schedule.sessionOverrides.map((o) => [isoDate(o.date), o]))
  const weekly = new Map(schedule.weeklySessions.map((s) => [s.weekday, s]))

  // Session window (UTC ms) labelled with a local date, or null when the venue doesn't open.
  const windows = new Map<string, readonly [number, number] | null>()
  const sessionFor = (date: string) => {
    if (windows.has(date)) return windows.get(date)!
    let window: readonly [number, number] | null = null
    const override = overrides.get(date)
    const hours = override ?? (holidays.has(date) ? undefined : weekly.get(weekdayOf(date)))
    if (hours) {
      // A session spanning midnight (GLBX overrides: 17:00 → 12:00) opens the day before.
      const openDate = hours.close <= hours.open ? shiftDate(date, -1) : date
      window = [
        fromZonedTime(`${openDate}T${hours.open}:00`, timezone).getTime(),
        fromZonedTime(`${date}T${hours.close}:00`, timezone).getTime(),
      ]
    }
    windows.set(date, window)
    return window
  }

  return (bar) => {
    const isFiller =
      !(bar.volume > 0) && bar.open === bar.high && bar.high === bar.low && bar.low === bar.close
    if (!regularHoursOnly && !isFiller) return false
    const start = bar.timeSec * 1000
    const end = start + intervalSec * 1000
    // Sessions labelled from the bar's first local day through the day after its last: an
    // overnight session is labelled with the day it closes, so it opens on the day before.
    const last = shiftDate(localDate(end - 1, timezone), 1)
    for (let date = localDate(start, timezone); date <= last; ) {
      const window = sessionFor(date)
      if (window && window[0] < end && window[1] > start) return false
      date = shiftDate(date, 1)
    }
    return true
  }
}
